import { useEffect, useRef, useState } from "react";
import { BadgeWithIconProps } from "../../data-model/badgeWithIconProps";
import ChevronDown from "../../svg/chevron-down";
import BadgeWithIcon from "../badgeWithIcon/badgeWithIcon";
import "../portal/portal.scss";
import PositionedPortal from "../portal/PositionedPortal";

interface BadgeDropdownProps {
  onClick?: (id: string) => void;
  selectedBadge: BadgeWithIconProps | null;
  badgeList?: BadgeWithIconProps[];
  shouldUsePortal?: boolean;
}

/**
 * Dropdown that lists status badges and emits the selected status id (medonc parity).
 *
 * @param props - Selected badge, options, and selection handler
 * @returns Badge trigger with portaled option list
 */
const BadgeDropdown = (props: BadgeDropdownProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownOptionsRef = useRef<HTMLDivElement>(null);
  const [selectedBadge, setSelectedBadge] = useState<BadgeWithIconProps | null>(
    props.selectedBadge ?? null,
  );

  const { shouldUsePortal = false } = props;

  useEffect(() => {
    if (props.selectedBadge) {
      setSelectedBadge(props.selectedBadge);
    }
  }, [props.selectedBadge]);

  /**
   * Update the visible badge when the user picks a new option.
   *
   * @param id - Selected badge id (patient status value)
   */
  const handleBadgeClick = (id: string) => {
    const selectedId = props.badgeList?.find((badge) => badge.id === id);
    if (selectedId) {
      setSelectedBadge(selectedId);
    }
  };

  /**
   * Commit the selection and close the menu.
   *
   * @param id - Selected badge id
   */
  const handleBadgeSelection = (id: string) => {
    handleBadgeClick(id);
    props.onClick?.(id);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownOptionsRef.current &&
        !dropdownOptionsRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderDropdownContent = () => (
    <div
      className="portal-dropdown flex flex-col gap-3 p-3"
      ref={dropdownOptionsRef}
    >
      <div
        className="flex flex-col gap-3"
        onClick={(event) => event.stopPropagation()}
      >
        {props.badgeList?.map((badge) => (
          <div
            className="w-full cursor-pointer rounded hover:bg-primaryGray-16"
            key={badge.id}
            onClick={() => handleBadgeSelection(badge.id)}
          >
            <BadgeWithIcon
              text={badge.text}
              id={badge.id}
              textColor={badge.textColor ?? "black"}
              bgColor={badge.bgColor ?? "white"}
              borderColor={badge.borderColor}
              onClick={() => {}}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="badge-drop-down__container relative w-max" ref={triggerRef}>
      <BadgeWithIcon
        text={selectedBadge?.text || ""}
        icon={<ChevronDown stroke={selectedBadge?.textColor || "black"} />}
        id={selectedBadge?.id || ""}
        textColor={selectedBadge?.textColor || "black"}
        bgColor={selectedBadge?.bgColor || "white"}
        borderColor={selectedBadge?.borderColor}
        onClick={() => setIsOpen((open) => !open)}
      />

      {isOpen &&
        props.badgeList &&
        props.badgeList.length > 0 &&
        (shouldUsePortal ? (
          <PositionedPortal triggerRef={triggerRef} isOpen={isOpen}>
            {renderDropdownContent()}
          </PositionedPortal>
        ) : (
          <div className="absolute left-0 top-full z-50">
            {renderDropdownContent()}
          </div>
        ))}
    </div>
  );
};

export default BadgeDropdown;
